import { Text } from '@/components/ui/text'
import { Loading } from '@components'
import { ActiveChip, ClearAllButton } from '@components/FilterControls'
import { InfiniteScrollSentinel } from '@components/InfiniteScrollSentinel'
import { useSearchParams } from '@solidjs/router'
import { userId } from '@state/auth'
import { PAGE_SIZE, searchRecordings } from '@state/recordings/api'
import { useRecordings } from '@state/recordings/state'
import { useInfiniteQuery } from '@tanstack/solid-query'
import dayjs from 'dayjs'
import { For, Show } from 'solid-js'
import { useService } from 'solid-services'
import { Box, Flex } from 'styled-system/jsx'
import { groupKey, groupLabel } from './Filters'
import Item from './Item'
import { recordingFilterDefaults, useState } from './state'

const PERIOD_DAYS = { '30d': 30, '90d': 90, '1y': 365 } as const
const PERIOD_LABEL = { '30d': 'Last 30 days', '90d': 'Last 3 months', '1y': 'Last year' } as const

/** Chips for the active search/filters; shown above both the grouped view and the results */
export function ActiveRecordingFilters() {
  const { filters, patchFilters, setSearch, clearFilters, isFiltering } = useService(useState)()
  const { discoveryRows } = useService(useRecordings)()
  const [, setParams] = useSearchParams()

  const groupName = () => {
    const row = discoveryRows().find((r) => groupKey(r) === filters().group)
    return row ? groupLabel(row.group_name) : 'Group'
  }

  return (
    <Show when={isFiltering()}>
      <Flex gap='1.5' flexWrap='wrap' alignItems='center'>
        <Show when={filters().search}>
          <ActiveChip label='search' onRemove={() => setSearch('')}>“{filters().search}”</ActiveChip>
        </Show>
        <Show when={filters().group}>
          <ActiveChip label='group' onRemove={() => patchFilters({ group: null })}>{groupName()}</ActiveChip>
        </Show>
        <Show when={filters().period !== 'any'}>
          <ActiveChip label='date range' onRemove={() => patchFilters({ period: 'any' })}>
            {PERIOD_LABEL[filters().period as keyof typeof PERIOD_LABEL]}
          </ActiveChip>
        </Show>
        <Show when={filters().evergreenOnly}>
          <ActiveChip label='evergreen' onRemove={() => patchFilters({ evergreenOnly: false })}>Evergreen</ActiveChip>
        </Show>
        <Show when={filters().sort !== recordingFilterDefaults.sort}>
          <ActiveChip label='sort' onRemove={() => patchFilters({ sort: 'newest' })}>Oldest first</ActiveChip>
        </Show>
        <ClearAllButton
          onClick={() => {
            clearFilters()
            setParams({ q: undefined }, { replace: true })
          }}
        />
      </Flex>
    </Show>
  )
}

export default function SearchResults() {
  const { filters } = useService(useState)()

  const query = useInfiniteQuery(() => ({
    queryKey: ['recordings-search', userId(), filters()],
    queryFn: ({ pageParam }) => {
      const f = filters()
      const days = f.period === 'any' ? null : PERIOD_DAYS[f.period]
      return searchRecordings(
        {
          search: f.search,
          group: f.group,
          since: days ? dayjs().subtract(days, 'day').format('YYYY-MM-DD') : null,
          evergreenOnly: f.evergreenOnly,
          ascending: f.sort === 'oldest',
        },
        pageParam as number,
      )
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length * PAGE_SIZE < lastPage.count ? allPages.length : undefined,
    placeholderData: (prev) => prev,
  }))

  const items = () => query.data?.pages.flatMap((p) => p.data) ?? []
  const count = () => query.data?.pages[0]?.count ?? 0

  return (
    <Box display='grid' gap='3'>
      <Text fontSize='xs' color='fg.subtle'>
        <Show when={!query.isPending} fallback='Searching…'>
          {count()} recording{count() === 1 ? '' : 's'}
        </Show>
      </Text>

      <Show when={query.isPending}>
        <Loading />
      </Show>

      <Show when={query.isError}>
        <Text color='error.10'>Failed to load recordings.</Text>
      </Show>

      <Show when={!query.isPending && items().length === 0}>
        <Text color='fg.muted' textAlign='center' py='8'>
          No recordings match. Try a different keyword or clear some filters.
        </Text>
      </Show>

      <Box
        display='grid'
        gap='3'
        gridTemplateColumns='repeat(auto-fill, 200px)'
        justifyContent={{ base: 'start', smDown: 'center' }}
      >
        <For each={items()}>{(recording) => <Item recording={recording} />}</For>
      </Box>

      <Show when={query.hasNextPage}>
        <InfiniteScrollSentinel onIntersect={() => query.fetchNextPage()} />
        <Show when={query.isFetchingNextPage}>
          <Loading />
        </Show>
      </Show>
    </Box>
  )
}
